import React, { useEffect, useState } from 'react';
import SectionCollapse from '../../../../../section/SectionCollapse';
import DataGrid from '../../../../../table/DataGrid';
import ButtonStartIcon from '../../../../../button/ButtonStartIcon';
import DialogAssessmentArea from '../CreateEditAssessmentArea/CreateEditAssessmentArea';
import IconTooltipButton from '../../../../../button/IconTooltipButton';
import { Chip, Menu } from '@mui/material';
import ListMenu from '../../../../../commom/ListMenu';
import useSubjects from '../../../../../../hooks/subjects/useSubjects';
import ResponsiveChips from '../../../../DataEntry/components/ResponsiveChips/ResponsiveChips';
import { onFindCode } from '../../../utils';

const ListAssessmentArea = ({ data = [], isLoading, isPreview, currentPeriodicalReport }) => {
  const { getAllSubjects } = useSubjects();
  const [showDialog, setShowDialog] = useState(null);
  const [menuEdit, setMenuEdit] = useState({ mouseX: null, mouseY: null });
  const previousPeriodicalReport = currentPeriodicalReport?.previousPeriodicalReport;
  const handleMenuClose = () => setMenuEdit({ mouseX: null, mouseY: null });
  const handleMenuOpen = (event, data) => {
    setMenuEdit({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      data
    });
  };

  useEffect(() => {
    getAllSubjects('all');
  }, [getAllSubjects]);

  const actions = isPreview ? null : (
    <>
      {currentPeriodicalReport?.status === 'draft' && (
        <ButtonStartIcon
          disabled={isLoading}
          onClick={() => setShowDialog({ type: 'generate-table', form: currentPeriodicalReport })}
          startIcon={'sync'}
          color='secondary'
          className='w-full'
        >
          Generate table
        </ButtonStartIcon>
      )}
      <div className='px-2' />
      <ButtonStartIcon
        disabled={isLoading}
        onClick={() => setShowDialog({ type: 'add', form: {} })}
        startIcon={['fal', 'plus']}
        color='secondary'
      >
        Add
      </ButtonStartIcon>
    </>
  );

  const dataSort = data
    ?.map((item) => ({ ...item, code: item.assessmentData.code }))
    ?.sort((a, b) => {
      if (a.code < b.code) {
        return -1;
      }
      if (a.code > b.code) {
        return 1;
      }
      return 0;
    });

  return (
    <>
      <SectionCollapse
        title={`assessment areas (${dataSort?.length || 0})`}
        classNameBody='absolute inset-x-0 bottom-0 top-12'
        actions={actions}
      >
        <DataGrid
          id='table-assessment-area'
          showSetting={false}
          columns={[
            {
              label: '#',
              key: 'index',
              show: true,
              hide: false,
              width: 10
            },
            { label: 'Code', key: 'code', show: true, hide: false },
            { label: 'Name', key: 'name', show: true, hide: false },
            {
              label: 'Type',
              key: 'type',
              show: true,
              hide: false,
              className: 'uppercase'
            },
            {
              label: 'Year level',
              key: 'yearLevels',
              show: true,
              hide: false
            },
            { label: 'Subjects', key: 'subjects', show: true, hide: false },
            {
              label: 'Previous code',
              key: 'prevCode',
              show: !!previousPeriodicalReport?._id,
              hide: false,
              width: 140
            },
            {
              label: 'Actions',
              key: 'actions',
              align: 'center',
              width: 50,
              show: true,
              hide: false
            }
          ]}
          groupBy={'curriculum'}
          rowsPerPage={data.length || 10}
          rows={dataSort?.map((row, index) => {
            const modelForm = {
              ...row,
              yearLevelId: row.yearLevel?.map((year) => year._id),
              subjectId: row.subject?.map((subject) => subject._id),
              codeSelected: {
                value: row?.assessmentData?.code,
                label: `${row?.assessmentData?.code} - ${row?.assessmentData?.name}`
              }
            };
            let prevCode = row?.previousAssessmentData?.map((item) => `${item?.code}`) || [];

            if (!row?.previousAssessmentData?.length && previousPeriodicalReport) {
              const find = onFindCode(row?.assessmentData, previousPeriodicalReport?.assessmentAreas || []);
              prevCode = find?.map((item) => `${item?.assessmentData?.code}`) || [];
            }
            return {
              ...row,
              index: index + 1,
              code: row?.code,
              type: row?.assessmentData?.type,
              name: row?.assessmentData?.name,
              curriculum: row?.curriculumName,
              curriculumId: row?.curriculumId,
              prevCode: (
                <div className='flex gap-1 items-center'>
                  {prevCode?.map((code, index) => (
                    <Chip key={`${code}-year-${index}`} size='small' label={code} />
                  ))}
                </div>
              ),
              yearLevels: (
                <div className='flex gap-1 items-center'>
                  {row.yearLevel?.map((year, index) => (
                    <Chip
                      variant='outlined'
                      key={`${row._id}-year-${index}`}
                      size='small'
                      label={year.name}
                    />
                  ))}
                </div>
              ),
              subjects: (
                <ResponsiveChips
                  minDisplay={3}
                  maxDisplay={10}
                  chipVariant='outlined'
                  items={row.subject}
                  field='name'
                />
              ),
              actions: (
                <div className='flex items-center gap-1 items-center justify-center'>
                  <IconTooltipButton
                    id={`preview-${row._id}`}
                    disabled={isLoading}
                    title='Preview'
                    icon='expand-alt'
                    onClick={() =>
                      setShowDialog({
                        type: 'preview',
                        form: modelForm
                      })
                    }
                  />
                  {['draft'].includes(currentPeriodicalReport?.status)  && currentPeriodicalReport?.isGeneratedMarking && (
                    <IconTooltipButton
                      id={`generate-table-${row._id}`}
                      disabled={isLoading}
                      title='Generate table'
                      icon='table'
                      onClick={(e) =>
                        setShowDialog({
                          type: 'generate-table-by',
                          form: modelForm
                        })
                      }
                    />
                  )}
                  {['open-for-marking'].includes(currentPeriodicalReport?.status) && (
                    <IconTooltipButton
                      id={`generate-table-${row._id}`}
                      disabled={isLoading}
                      title='Sync marking data'
                      icon='sync'
                      onClick={(e) =>
                        setShowDialog({
                          type: 'sync-marking-by',
                          form: modelForm
                        })
                      }
                    />
                  )}
                  {!isPreview && (
                    <>
                      <IconTooltipButton
                        id={`edit-${row._id}`}
                        disabled={isLoading}
                        title='Edit'
                        icon='pen'
                        onClick={(e) => handleMenuOpen(e, modelForm)}
                      />
                      <IconTooltipButton
                        id={`delete-${row._id}`}
                        disabled={isLoading}
                        title='Delete'
                        icon='trash-alt'
                        onClick={() =>
                          setShowDialog({
                            type: 'delete',
                            form: modelForm
                          })
                        }
                      />
                    </>
                  )}
                </div>
              )
            };
          })}
          labelNoRows="You don't have any assessment areas yet"
        />
      </SectionCollapse>
      {showDialog && (
        <DialogAssessmentArea
          show={!!showDialog}
          type={showDialog.type}
          formData={showDialog.form}
          onClose={() => setShowDialog(null)}
          data={data}
          codes={dataSort?.map((item) => ({
            code: item.code,
            curriculumId: item.curriculumId
          }))}
        />
      )}
      {menuEdit && (
        <Menu
          id={`menuEdit-${data?._id}`}
          MenuListProps={{
            style: { width: '260px' },
            className: 'list-menuEdit p-0'
          }}
          keepMounted
          open={menuEdit.mouseY !== null}
          onClose={handleMenuClose}
          anchorReference='anchorPosition'
          anchorPosition={
            menuEdit.mouseY !== null && menuEdit.mouseX !== null
              ? { top: menuEdit.mouseY, left: menuEdit.mouseX }
              : undefined
          }
        >
          <ListMenu
            id={`menuEdit-list-${data?._id}`}
            items={[
              {
                label: 'Edit basic information',
                permissions: true,
                onClick: () => setShowDialog({ type: 'edit-basic-information', form: menuEdit.data })
              },
              {
                label: 'Edit subject',
                permissions: true,
                onClick: () => setShowDialog({ type: 'edit-subject', form: menuEdit.data })
              }
            ]
              .filter((item) => item.permissions)
              .map((item) => ({
                ...item,
                onClick: () => {
                  if (item.onClick) {
                    item.onClick();
                  }
                  handleMenuClose();
                }
              }))}
          />
        </Menu>
      )}
    </>
  );
};

export default ListAssessmentArea;
